import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';

import {
  ActionSheetButton,
  ActionSheetController,
  ToastController,
} from '@ionic/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FileAccess } from '@wilson/interfaces';
import {
  faPaperclip,
  faFileLock,
  faFilePdf,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Browser } from '@capacitor/browser';
import {
  ShiftAttachmentsViewerDismissRole,
  ShiftAttachmentViewerHelperService,
} from '@wilson/shared-components';
import { Capacitor } from '@capacitor/core';
import { NativeFileSaverHelperService } from '@wilson/files';
import { downloadFile, isAndroidWithoutPdfSupport } from '@wilson/utils';
import * as Sentry from '@sentry/capacitor';
import { IonicModule } from '@ionic/angular/lazy';

@Component({
  selector: 'wilson-worktime-pdf-timesheet-card',
  standalone: true,
  imports: [TranslatePipe, FontAwesomeModule, IonicModule],
  providers: [ShiftAttachmentViewerHelperService],
  templateUrl: './worktime-time-pdf-timesheet-card.component.html',
  styleUrl: './worktime-time-pdf-timesheet-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorktimePdfTimesheetCardComponent {
  readonly isLoading = input<boolean>(false);
  readonly userTimeSheetFileAccessInput = input<FileAccess | null>(null);
  readonly fileName = input<string | null>('');

  private readonly translate = inject(TranslateService);
  actionSheetController = inject(ActionSheetController);
  private readonly toastController = inject(ToastController);
  private readonly nativeFileSaverHelperService = inject(
    NativeFileSaverHelperService,
  );
  protected shiftAttachmentViewerHelperService = inject(
    ShiftAttachmentViewerHelperService,
  );

  readonly faPaperclip = faPaperclip;
  readonly faFileLock = faFileLock;
  readonly faFilePdf = faFilePdf;
  protected faChevronRight = faChevronRight;

  protected async presentActionSheet(): Promise<void> {
    const buttons: ActionSheetButton[] = [
      {
        text: this.translate.instant('general.cancel'),
        role: 'cancel',
      },
    ];

    if (this.fileName()?.length) {
      buttons.unshift({
        text: this.translate.instant(
          'shift.attachmentsModal.download_attachment',
        ),
        handler: (): void => {
          this.downloadAttachment();
        },
      });
    }

    if (this.fileName()?.length) {
      buttons.unshift({
        text: this.translate.instant('shift.attachmentsModal.view_attachment'),
        handler: async () => {
          const lacksPdfSupport = await isAndroidWithoutPdfSupport();
          if (lacksPdfSupport) {
            const role =
              await this.shiftAttachmentViewerHelperService.viewAttachment(
                this.userTimeSheetFileAccessInput()?.accessUrl as string,
              );

            if (role === ShiftAttachmentsViewerDismissRole.Download) {
              this.downloadAttachment();
            }
          } else {
            this.openAttachment();
          }
        },
      });
    }

    const actionSheet = await this.actionSheetController.create({
      buttons,
    });
    await actionSheet.present();
  }

  private async openAttachment(): Promise<void> {
    await Browser.open({
      url: this.userTimeSheetFileAccessInput()?.accessUrl as string,
    });
  }

  private async downloadAttachment(): Promise<void> {
    const url = this.userTimeSheetFileAccessInput()?.accessUrl;

    if (url) {
      try {
        const response = await fetch(url);

        const blob = await response.blob();
        const fileName = this.fileName() || '';

        if (Capacitor.isNativePlatform()) {
          await this.nativeFileSaverHelperService.saveFile(
            blob,
            fileName,
            'pdf',
          );
        } else {
          downloadFile(blob, fileName, 'pdf');
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
        const toast = await this.toastController.create({
          message: this.translate.instant('general.errorMessage'),
          duration: 3000,
          position: 'bottom',
        });
        await toast.present();
      }
    }
  }
}
